/*
 * KIYO: photos in blog post text.
 * Runs on elements with [data-kiyo-photo-strips] (the post body in main-article.liquid).
 * Photos the merchant inserts next to each other become a carousel (<kiyo-photo-strip>)
 * modelled on Into Archive's editorial body galleries: a full-width band, each photo
 * about half the width (most of it on phones) with the next one cut off at the edge,
 * moved by swiping, trackpad, mouse drag or the bar indicators underneath. A photo on
 * its own is shown like the header photo.
 * Styles: assets/kiyo-photo-strip.css.
 */

(function () {
  const IMAGE_WIDTHS = [600, 900, 1200, 1600, 2000];

  // An element counts as "just photos" when it holds images and no real text.
  function photosIn(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    if (node.tagName === 'IMG') return [node];
    const images = [...node.querySelectorAll('img')];
    if (!images.length) return null;
    if (node.textContent.trim() !== '') return null;
    if (node.querySelector('iframe, video, table, ul, ol, h1, h2, h3, h4, h5, h6')) return null;
    return images;
  }

  function sizedSrc(src, width) {
    try {
      const url = new URL(src, window.location.href);
      if (!url.pathname.includes('/cdn/shop/') && !url.hostname.includes('shopify')) return null;
      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return null;
    }
  }

  function prepareImage(original, sizes, eager) {
    const img = document.createElement('img');
    img.src = original.getAttribute('src');
    img.alt = original.getAttribute('alt') || '';
    img.loading = eager ? 'eager' : 'lazy';
    img.decoding = 'async';
    const srcset = IMAGE_WIDTHS.map((w) => {
      const url = sizedSrc(img.src, w);
      return url ? `${url} ${w}w` : null;
    }).filter(Boolean);
    if (srcset.length) {
      img.srcset = srcset.join(', ');
      img.sizes = sizes;
    }
    return img;
  }

  // Width / height, known up front from attributes or after the image loads.
  function setRatio(holder, original, img) {
    const w = parseFloat(original.getAttribute('width'));
    const h = parseFloat(original.getAttribute('height'));
    if (w && h) holder.style.setProperty('--kiyo-ratio', w / h);
    const apply = () => {
      if (img.naturalWidth && img.naturalHeight) {
        holder.style.setProperty('--kiyo-ratio', img.naturalWidth / img.naturalHeight);
        holder.dispatchEvent(new CustomEvent('kiyo:resized', { bubbles: true }));
      }
    };
    if (img.complete) apply();
    else img.addEventListener('load', apply, { once: true });
  }

  function buildSingle(original) {
    const figure = document.createElement('figure');
    figure.className = 'kiyo-photo';
    const img = prepareImage(original, '(min-width: 750px) 726px, 100vw');
    setRatio(figure, original, img);
    figure.appendChild(img);
    return figure;
  }

  function buildStrip(originals) {
    const strip = document.createElement('kiyo-photo-strip');
    strip.className = 'kiyo-strip';
    strip.setAttribute('role', 'region');
    strip.setAttribute('aria-roledescription', 'carousel');
    strip.setAttribute('aria-label', `Photo gallery, ${originals.length} photos`);

    const track = document.createElement('div');
    track.className = 'kiyo-strip__track';
    track.tabIndex = 0;

    originals.forEach((original, i) => {
      const slide = document.createElement('figure');
      slide.className = 'kiyo-strip__slide';
      slide.setAttribute('aria-label', `${i + 1} / ${originals.length}`);
      const frame = document.createElement('div');
      frame.className = 'kiyo-strip__frame';
      const img = prepareImage(original, '(min-width: 990px) 33vw, (min-width: 750px) 50vw, 75vw', i < 3);
      img.draggable = false;
      frame.appendChild(img);
      slide.appendChild(frame);
      track.appendChild(slide);
    });

    const dots = document.createElement('div');
    dots.className = 'kiyo-strip__dots';

    const inner = document.createElement('div');
    inner.className = 'kiyo-strip__inner';
    inner.append(track, dots);
    strip.appendChild(inner);
    return strip;
  }

  function enhance(container) {
    if (container.dataset.kiyoPhotoStripsReady) return;
    container.dataset.kiyoPhotoStripsReady = 'true';

    const groups = [];
    let current = null;
    [...container.children].forEach((child) => {
      const images = photosIn(child);
      if (images) {
        if (!current) {
          current = { nodes: [], images: [] };
          groups.push(current);
        }
        current.nodes.push(child);
        current.images.push(...images);
      } else if (child.tagName === 'BR' || (child.tagName === 'P' && child.innerHTML.trim() === '&nbsp;')) {
        // Blank lines between photos don't break a group.
        if (current) current.nodes.push(child);
      } else {
        current = null;
      }
    });

    groups.forEach(({ nodes, images }) => {
      const replacement = images.length > 1 ? buildStrip(images) : buildSingle(images[0]);
      nodes[0].before(replacement);
      nodes.forEach((node) => node.remove());
    });
  }

  if (!customElements.get('kiyo-photo-strip')) {
    customElements.define(
      'kiyo-photo-strip',
      class extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          this.track = this.querySelector('.kiyo-strip__track');
          this.dotsWrap = this.querySelector('.kiyo-strip__dots');
          this.slides = [...this.querySelectorAll('.kiyo-strip__slide')];

          this.track.addEventListener('scroll', () => {
            cancelAnimationFrame(this.frame);
            this.frame = requestAnimationFrame(() => this.updateDots());
          });
          this.track.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') this.goTo(this.active + 1);
            if (event.key === 'ArrowLeft') this.goTo(this.active - 1);
          });
          this.enableMouseDrag();

          // Snap points change as photos load and when the window resizes.
          const refresh = () => this.buildDots();
          this.slides.forEach((slide) => {
            const img = slide.querySelector('img');
            if (!img.complete) img.addEventListener('load', refresh, { once: true });
          });
          window.addEventListener('resize', refresh);
          refresh();
        }

        // Where each photo lines up with the left edge, trimmed so the last stop is the end
        // of the strip (two photos that both fit near the end share one stop).
        snapPoints() {
          const max = this.track.scrollWidth - this.track.clientWidth;
          const start = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
          const points = [];
          this.slides.forEach((slide) => {
            const point = Math.max(0, Math.min(slide.offsetLeft - start, max));
            if (!points.length || point - points[points.length - 1] > 2) points.push(point);
          });
          return points;
        }

        buildDots() {
          this.points = this.snapPoints();
          if (this.points.length === (this.dots || []).length) return this.updateDots();
          this.dotsWrap.innerHTML = '';
          this.dots = this.points.map((_, i) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'kiyo-strip__dot';
            dot.setAttribute('aria-label', `View image ${i + 1} of ${this.points.length}`);
            dot.addEventListener('click', () => this.goTo(i));
            this.dotsWrap.appendChild(dot);
            return dot;
          });
          this.dotsWrap.hidden = this.points.length < 2;
          this.updateDots();
        }

        updateDots() {
          if (!this.points) return;
          const x = this.track.scrollLeft;
          let closest = 0;
          this.points.forEach((point, i) => {
            if (Math.abs(point - x) < Math.abs(this.points[closest] - x)) closest = i;
          });
          this.active = closest;
          this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === closest));
        }

        goTo(index) {
          const i = Math.max(0, Math.min(index, this.points.length - 1));
          this.track.scrollTo({ left: this.points[i], behavior: 'smooth' });
        }

        // Click-and-drag with a mouse, like a touch swipe; touch and trackpads scroll natively.
        enableMouseDrag() {
          let startX = 0;
          let startScroll = 0;
          let moved = false;
          this.track.addEventListener('pointerdown', (event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return;
            startX = event.clientX;
            startScroll = this.track.scrollLeft;
            moved = false;
            this.track.classList.add('is-dragging');
            this.track.setPointerCapture(event.pointerId);
          });
          this.track.addEventListener('pointermove', (event) => {
            if (!this.track.classList.contains('is-dragging')) return;
            const dx = event.clientX - startX;
            if (Math.abs(dx) > 3) moved = true;
            this.track.scrollLeft = startScroll - dx;
          });
          const end = (event) => {
            if (!this.track.classList.contains('is-dragging')) return;
            this.track.classList.remove('is-dragging');
            const dx = event.clientX - startX;
            // Settle on the next/previous stop in the drag direction.
            const from = this.points.findIndex((p) => p >= startScroll - 2);
            let target = from === -1 ? this.points.length - 1 : from;
            if (dx < -40) target += 1;
            if (dx > 40) target -= 1;
            this.goTo(target);
          };
          this.track.addEventListener('pointerup', end);
          this.track.addEventListener('pointercancel', end);
          this.track.addEventListener('click', (event) => {
            if (moved) {
              event.preventDefault();
              event.stopPropagation();
              moved = false;
            }
          }, true);
        }
      }
    );
  }

  function init() {
    document.querySelectorAll('[data-kiyo-photo-strips]').forEach(enhance);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
